import { EntityHeader } from '@pesto/features/src/EntityProfile/components/EntityHeader';
import { useUrlTabs } from '@pesto/hooks';
import { Card, LoadingSpinner, type OverflowTabItem, OverflowTabs } from '@pesto/ui';
import { useParams } from 'react-router-dom';

import { useGetLoanByIdQuery } from '../../__generated__/graphql/api';
import { isSimulatedPurchaseEnv } from '../../constants/global-env';
import { routes } from '../../constants/routes';
import { useRole } from '../../hooks/useRole';
import { NotFound } from '../NotFound';
import { LoanAchRequestsTab } from './components/LoanAchRequestsTab/LoanAchRequestsTab';
import { LoanAutopaySettings } from './components/LoanAutopaySettings';
import { LoanCardsTab } from './components/LoanCardsTab';
import { LoanOverviewTab } from './components/LoanOverviewTab';
import { LoanStatementsTab } from './components/LoanStatementsTab/LoanStatementsTab';
import { LoanTransactionsTab } from './components/LoanTransactionsTab/LoanTransactionsTab';
import { LoanUsersTab } from './components/LoanUsersTab';
import { SimulatePurchaseButton } from './components/SimulatePurchase/SimulatePurchaseButton';
import { buildLoanHeaderConfig } from './loanHeaderConfig';
import { toLoanDetail } from './toLoanDetail';

const countItems = <T,>(items: readonly (T | null | undefined)[] | null | undefined) =>
  items?.filter(Boolean).length ?? 0;

export const LoanDetailsPage = () => {
  const { id, statementId } = useParams<{ id: string; statementId?: string }>();
  const { isPartnerAdmin } = useRole();

  const { data, loading } = useGetLoanByIdQuery({
    variables: { loanId: id ?? '' },
    skip: !id,
    notifyOnNetworkStatusChange: true,
  });

  const loan = data?.loan ? toLoanDetail(data.loan) : null;
  const loanId = loan?.id ?? '';
  const statementCount = countItems(data?.loan?.statements);
  const achRequestCount = countItems(data?.loan?.achRequests);

  const primary: OverflowTabItem[] = [
    {
      label: 'Overview',
      value: 'overview',
      component: loan ? <LoanOverviewTab loan={loan} /> : null,
    },
    {
      label: 'Statements',
      value: 'statements',
      component: loan ? (
        <LoanStatementsTab
          loanId={loanId}
          statementId={statementId}
          statementsBasePath={`${routes.loans}/${loanId}/statements`}
        />
      ) : null,
      count: statementCount,
    },
    {
      label: 'ACH Requests',
      value: 'ach-requests',
      component: loan ? <LoanAchRequestsTab loanId={loanId} /> : null,
      count: achRequestCount,
    },
    {
      label: 'Transactions',
      value: 'transactions',
      component: loan ? <LoanTransactionsTab loanId={loanId} /> : null,
    },
    {
      label: 'Auto-pay',
      value: 'auto-pay',
      component: loan ? <LoanAutopaySettings loanId={loanId} /> : null,
    },
    {
      label: 'Users',
      value: 'users',
      component: loan ? <LoanUsersTab users={loan.users} /> : null,
      count: loan?.users.length,
    },
    {
      label: 'Cards',
      value: 'cards',
      component: loan ? <LoanCardsTab cards={loan.cards} /> : null,
      count: loan?.cards.length,
    },
  ];

  const validTabs = primary.map((item) => item.value);

  const { currentTab, setCurrentTab } = useUrlTabs({
    defaultTab: 'overview',
    validTabs,
    basePath: `${routes.loans}/${id ?? ''}`,
  });

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!loan) {
    return <NotFound />;
  }

  const offersSimulatedPurchases = isPartnerAdmin && isSimulatedPurchaseEnv() && loan.cards.length > 0;
  const headerActions = offersSimulatedPurchases ? <SimulatePurchaseButton cards={loan.cards} /> : undefined;

  return (
    <main className="flex flex-1 flex-col gap-4 p-4 md:p-8">
      <Card>
        <EntityHeader config={buildLoanHeaderConfig({ loan, actions: headerActions })} />
      </Card>
      <OverflowTabs primary={primary} value={currentTab} onValueChange={setCurrentTab} />
    </main>
  );
};
